import { Grid, Link, Typography } from "@mui/material";
import { useState } from "react";
import { DetailPageTopSection } from "~/components/DetailPageLayouts/DetailPageTopSection";
import { ScoreRating } from "~/operations";
import { FindingSubheader } from "~/pages/findings/FindingOnAsset/utils";
import { PlatformCheckScoreBlock } from "./PlatformCheckScoreBlock";
import { CheckData, CheckScoreData, BlastRadiusData } from "../types";

interface PlatformCheckOverviewProps {
  title: string;
  isLoading: boolean;
  check?: CheckData | null;
  checkScore?: CheckScoreData | null;
  blastRadius?: BlastRadiusData | null;
  lastScannedAt?: string | null;
  policies: string[];
}

export function PlatformCheckOverview({
  title,
  isLoading,
  check,
  checkScore,
  blastRadius,
  lastScannedAt,
  policies,
}: PlatformCheckOverviewProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const description = check?.docs?.desc || "";
  const shouldTruncateDescription = description.length > 300;
  const displayedDescription =
    showFullDescription || !shouldTruncateDescription
      ? description
      : description.slice(0, 300) + "...";

  const riskFactorsCount =
    checkScore?.riskFactors?.filter((rf) => rf?.affected)?.length || 0;
  const failingAssets = blastRadius?.affected || 0;
  const passingAssets =
    blastRadius?.assets != null
      ? blastRadius.assets - (blastRadius.affected ?? 0)
      : 0;
  const exceptions = (blastRadius?.snoozed || 0) + (blastRadius?.disabled || 0);

  const policiesCount = policies.length;
  const policiesLabel = policiesCount === 1 ? "Policy" : "Policies";

  const subheaders: FindingSubheader[] = [
    ...(policiesCount > 0
      ? [
          {
            id: "policies",
            label: "Check on",
            value: `${policiesCount} ${policiesLabel}`,
          },
        ]
      : []),
    {
      id: "assets",
      label: "evaluating",
      value: `${blastRadius?.assets || 0} Assets`,
    },
    ...(lastScannedAt
      ? [
          {
            id: "last-updated",
            label: "last updated",
            value: lastScannedAt,
          },
        ]
      : []),
  ];

  return (
    <DetailPageTopSection
      content={{
        triageBar: {
          title,
          fallbackPath: "/policies",
          subheaders,
          toolbarItems: [],
          triage: false,
          isLoading: isLoading,
          showBackButton: false,
          compact: true,
        },
        sectionHeading: "Check Overview",
        stats: [],
        left: {
          upper: (
            <Grid size={12}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 0,
                  color: "text.primary",
                }}
              >
                DESCRIPTION
              </Typography>
              <Typography sx={{ mt: 1, mb: 1 }}>
                {displayedDescription}
              </Typography>
              {shouldTruncateDescription && (
                <Link
                  component="button"
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  sx={{ cursor: "pointer" }}
                >
                  {showFullDescription ? "Show less" : "View full description"}
                </Link>
              )}
            </Grid>
          ),
          lower: (
            <Grid size={12}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 0,
                  color: "text.primary",
                  textTransform: "uppercase",
                }}
              >
                {policiesLabel}
              </Typography>
              <Typography sx={{ mt: 1 }}>
                {policies.length > 0 ? policies.join(", ") : "---"}
              </Typography>
            </Grid>
          ),
        },
        right: (
          <PlatformCheckScoreBlock
            rating={checkScore?.rating || ScoreRating.Unscored}
            stats={[
              { label: "Risk Factors", value: riskFactorsCount },
              { label: "Failing Assets", value: failingAssets },
              { label: "Passing Assets", value: passingAssets },
              { label: "Exceptions", value: exceptions },
            ]}
          />
        ),
      }}
    />
  );
}
